import { Component, computed, effect, inject, input, signal, WritableSignal } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MeetingBot, PrivacyLevel, PrivacyOverride } from '@vendasta/protos/meeting_analysis/v1';
import { overridePrivacy$ } from '@vendasta/protos/meeting_analysis/v1/meeting-bot-service';
import { SourcePipe } from '../meeting-list-item/source/source.pipe';
import { AvatarGroupComponent } from '@vendasta/galaxy/avatar';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatSelectModule } from '@angular/material/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { toObservable } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { IAMService, UserIdentifier } from '@vendasta/iamv2';
import { getFormattedDisplayName } from '../../users.utility';
import { of, switchMap, take } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { MatDialog } from '@angular/material/dialog';
import { RecordingInfoDialogComponent } from './recording-info-dialog/recording-info-dialog.component';

@Component({
  selector: 'meeting-analysis-meeting-bot-list-item',
  templateUrl: './meeting-bot-list-item.component.html',
  styleUrls: ['../meeting-list-item/meeting-list-item.component.scss', './meeting-bot-list-item.component.scss'],
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatButtonModule,
    RouterModule,
    TranslateModule,
    DatePipe,
    SourcePipe,
    SourcePipe,
    AvatarGroupComponent,
    GalaxyFormFieldModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
  ],
})
export class MeetingBotListItemComponent {
  private iamService = inject(IAMService);
  private readonly alertService = inject(SnackbarService);
  private readonly dialog = inject(MatDialog);
  meetingBot = input.required<MeetingBot>();
  avatarList = computed(() => {
    const meetingBot = this.meetingBot();
    return (
      meetingBot?.eventInvitees
        ?.map((invitee) => ({
          name: invitee.displayName || invitee.email,
        }))
        .sort() || []
    );
  });

  overriddenPrivacy: WritableSignal<PrivacyOverride | null> = signal(null);
  privacyOverride = computed(() => {
    const meetingBot = this.meetingBot();
    const overriddenPrivacy = this.overriddenPrivacy();
    if (overriddenPrivacy !== null) {
      return overriddenPrivacy;
    }
    return meetingBot?.privacyOverride;
  });
  isPrivacyLevelOverridden = computed(() => {
    const privacyOverride = this.privacyOverride();
    return privacyOverride?.privacyLevel === PrivacyLevel.PRIVACY_LEVEL_PRIVATE;
  });
  overridingPrivacy = signal(false);

  userThatOverrodePrivacy$ = toObservable(this.overriddenPrivacy).pipe(
    switchMap((privacyOverride) => {
      if (privacyOverride?.userId) {
        return this.iamService.getUser({ userId: privacyOverride.userId } as UserIdentifier);
      }
      return of(null);
    }),
    map((user) => {
      if (user) {
        return getFormattedDisplayName(user);
      }
      return '';
    }),
  );
  privacyOverrideDate = computed(() => {
    const privacyOverride = this.privacyOverride();
    if (privacyOverride?.updated) {
      return privacyOverride.updated;
    }
    return null;
  });

  constructor() {
    effect(() => {
      this.overriddenPrivacy.set(this.meetingBot().privacyOverride);
    });
  }

  changePrivacyOverride(level: boolean) {
    let privacyLevel: PrivacyLevel = PrivacyLevel.PRIVACY_LEVEL_UNSPECIFIED;
    if (level) {
      privacyLevel = PrivacyLevel.PRIVACY_LEVEL_PRIVATE;
    }
    this.overridingPrivacy.set(true);
    overridePrivacy$({
      meetingBotId: this.meetingBot().id,
      privacyLevel: privacyLevel,
    })
      .pipe(take(1))
      .subscribe({
        next: (resp) => {
          this.overriddenPrivacy.set(resp.privacyOverride);
          this.overridingPrivacy.set(false);
          this.alertService.openSuccessSnack('VISIBILITY.SUCCESS_CHANGING');
        },
        error: (err) => {
          console.error('Error overriding privacy level', err);
          this.alertService.openErrorSnack('RECORDING_VISIBILITY.ERROR_OVERRIDING_PRIVACY');
          this.overridingPrivacy.set(false);
        },
      });
  }

  openRecordingInfoDialog() {
    this.dialog.open(RecordingInfoDialogComponent, {
      width: '500px',
    });
  }
}
